import { useMutation } from '@tanstack/react-query';
import { createQuoteV2SectionMutation } from '@setvi/shared/services/react-query';
import { moveQuoteV2ItemsMutation } from 'Services/Mutations/QuotesV2';
import { useQuoteCache } from './use-quote-cache';

export interface ReorderMove {
  itemId: string;
  position: string;
  targetSectionId: string | null;
}

/**
 * A section that doesn't exist yet on the server. It's created first so its
 * real id can replace `tempId` wherever a move targets it. `tempId` is the
 * client-generated id used while the reorder is staged in the dialog.
 */
export interface NewSectionSpec {
  tempId: string;
  name: string;
  note: string;
  position: string;
}

interface ReorderItemsVariables {
  moves: ReorderMove[];
  newSections?: NewSectionSpec[];
}

/**
 * Reorder items via V2 move endpoint with fractional indexing.
 * Only sends changed positions instead of replacing the full items array.
 *
 * When `newSections` is provided, each section is created first (v2 section
 * endpoint) and its returned id replaces the temp id in any move that targets
 * it, so items can be moved into a brand-new section in a single save.
 */
export const useReorderItems = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = moveQuoteV2ItemsMutation();
  const { mutationFn: createSection } = createQuoteV2SectionMutation();

  return useMutation({
    mutationFn: async ({ moves, newSections = [] }: ReorderItemsVariables) => {
      // Create every new section first (in parallel) so their real ids can
      // replace the temp ids that moves target.
      const created = await Promise.all(
        newSections.map(section =>
          createSection({
            quoteId,
            name: section.name,
            note: section.note,
            position: section.position
          }).then(response => ({
            tempId: section.tempId,
            realId: (response as any)?.item?._id || (response as any)?._id
          }))
        )
      );

      const tempIdToRealId = new Map<string, string>();
      created.forEach(({ tempId, realId }) => {
        if (realId) tempIdToRealId.set(tempId, realId);
      });

      const resolvedMoves = moves.map(move => {
        const realId = move.targetSectionId
          ? tempIdToRealId.get(move.targetSectionId)
          : undefined;
        return realId ? { ...move, targetSectionId: realId } : move;
      });

      return mutationFn({ quoteId, moves: resolvedMoves });
    },

    onMutate: async () => {
      await cache.cancel();

      const previousQuote = cache.getQuote();
      const previousItems = cache.getItems();

      return { previousQuote, previousItems };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousQuote) {
        cache.setQuote(() => context.previousQuote);
      }
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: () => {
      cache.refetch();
    }
  });
};
